// DEPENDENCIES ---------------------------------------------------------------- //

import React from 'react';
import { Box, Typography } from '@mui/material';
import {
  BiMailSend,
  BiMessage,
  BiPhone,
  BiPhoneCall,
} from 'react-icons/bi';
import { Theme, useTheme } from '@mui/material';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';

dayjs.extend(relativeTime);

// TYPES ---------------------------------------------------------------- //

export interface FlowStep {
  type: string;
  ordinality: number;
  delayMinutes?: number;
  expirationMinutes?: number;
}

interface FlowVisualizationProps {
  flowSteps: FlowStep[];
  currentAttempt: number;
  totalAttempts: number;
  lastSentTime?: string;
}

// HELPERS ---------------------------------------------------------------- //

const getNotificationIcon = (type: string | undefined) => {
  const normalizedType = type?.toLowerCase() || '';
  if (normalizedType.includes('email')) {
    return BiMailSend;
  }
  if (normalizedType.includes('sms')) {
    return BiMessage;
  }
  if (normalizedType.includes('phone-manual') || normalizedType.includes('phone_manual')) {
    return BiPhoneCall;
  }
  if (normalizedType.includes('phone')) {
    return BiPhone;
  }
  return BiMailSend; // Default
};

const getNotificationColor = (type: string | undefined, isCompleted: boolean, theme: Theme) => {
  if (isCompleted) {
    return theme.palette.text.disabled; // Gray for completed
  }
  const normalizedType = type?.toLowerCase() || '';
  if (normalizedType.includes('email')) {
    return '#30a0e0'; // Blue for email
  }
  if (normalizedType.includes('sms')) {
    return '#30b448'; // Green for SMS
  }
  if (
    normalizedType.includes('phone-manual') ||
    normalizedType.includes('phone_manual') ||
    normalizedType.includes('phone')
  ) {
    return '#ff4830'; // Red for phone
  }
  return '#4068a0'; // Default blue
};

export const formatRelativeTime = (dateString: string): string => {
  const date = dayjs(dateString);
  const now = dayjs();
  const diffMins = now.diff(date, 'minute');
  const diffHours = now.diff(date, 'hour');
  const diffDays = now.diff(date, 'day');

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays === 1) return 'Yesterday';
  if (diffDays < 7) return `${diffDays}d ago`;
  return date.format('MMM D');
};

// COMPONENT ---------------------------------------------------------------- //

const FlowVisualization: React.FC<FlowVisualizationProps> = ({
  flowSteps,
  currentAttempt,
  totalAttempts,
  lastSentTime,
}) => {
  const theme = useTheme();

  // Fallback display when no flow steps are defined
  if (flowSteps.length === 0) {
    return (
      <Box
        sx={{
          display: 'flex',
          gap: 0.75,
          fontSize: '13px',
        }}
      >
        <Typography sx={{ color: 'text.secondary', fontSize: '13px' }}>Attempts:</Typography>
        <Typography sx={{ fontWeight: 600, fontSize: '13px' }}>
          {currentAttempt} of {totalAttempts}
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        overflowX: 'auto',
        '&::-webkit-scrollbar': {
          height: 4,
        },
        '&::-webkit-scrollbar-thumb': {
          bgcolor: 'divider',
          borderRadius: 2,
        },
      }}
    >
      {/* Flow steps */}
      {flowSteps.map((step, index) => {
        const stepNumber = index + 1;
        const isCompleted = stepNumber <= currentAttempt;
        const IconComponent = getNotificationIcon(step.type);
        const iconColor = getNotificationColor(step.type, isCompleted, theme);
        const stepType = step.type || 'email';
        const isLast = index === flowSteps.length - 1;

        return (
          <React.Fragment key={step.ordinality || index}>
            {/* Step icon */}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 32,
                height: 32,
                borderRadius: '50%',
                bgcolor: isCompleted ? theme.palette.grey[200] : 'white',
                border: isCompleted ? '1px solid #ddd' : '2px solid',
                borderColor: isCompleted ? '#ddd' : iconColor,
                position: 'relative',
                transition: 'all 0.2s ease',
                zIndex: flowSteps.length - index,
                opacity: isCompleted ? 0.5 : 1,
                flexShrink: 0,
              }}
              title={`${stepType}${step.delayMinutes ? ` (${step.delayMinutes}min delay)` : ''}${step.expirationMinutes ? ` (${step.expirationMinutes}min expiry)` : ''}`}
            >
              <IconComponent
                size={18}
                color={iconColor}
                style={{
                  transition: 'all 0.2s ease',
                }}
              />
            </Box>

            {/* Connector line */}
            {!isLast && (
              <Box
                sx={{
                  width: 20,
                  height: 2,
                  bgcolor: isCompleted ? '#ddd' : '#e0e0e0',
                  ml: -0.5,
                  mr: -0.5,
                  position: 'relative',
                  zIndex: 0,
                  flexShrink: 0,
                }}
              />
            )}
          </React.Fragment>
        );
      })}
    </Box>
  );
};

// EXPORT ---------------------------------------------------------------- //

export default FlowVisualization;

